import { MouseEvent, useEffect, useState } from 'react';

import { StepProps, useQuery } from '@betterment/js-runtime';
import { Banner, Box, Button, CenteredLayout, Heading, Label, PancakeStack, Text } from '@betterment/samba-react';
import copy from 'copy-to-clipboard';
import { QRCodeSVG } from 'qrcode.react';
import { Helmet } from 'react-helmet';

import { TotpSetupNewResponse } from './data';

import { SambaAutoLink } from '../../../b4b/components/SambaAutoLink';
import { FlowHeaderWithSettings } from '../../Shared/Components/FlowHeaderWithSettings';
import urlBuilder from '../../Shared/url-builder';
import { TestIDProps } from '../../testing/test-id-props';

const StartStep = ({ goToNextStep, testID }: StepProps & TestIDProps) => {
  const {
    data: newQueryData,
    loading: newQueryLoading, // eslint-disable-line @typescript-eslint/naming-convention
    errors: newQueryErrors,
    networkError: newQueryNetworkError,
  } = useQuery<TotpSetupNewResponse>(urlBuilder.getTotpSetupNew.absolute());
  const [totpProvisioningURI, setTOTPProvisioningURI] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const clickContinue = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    goToNextStep();
  };
  useEffect(() => {
    if (newQueryLoading) {
      return;
    }
    if (newQueryErrors) {
      const mError = 'System error';
      setError(mError);
      return;
    }
    const { totpProvisioningUri: newTOTPProvisioningURI } = newQueryData as TotpSetupNewResponse;
    if (newTOTPProvisioningURI === undefined) {
      const mError = 'System error';
      setError(mError);
      return;
    }
    setTOTPProvisioningURI(newTOTPProvisioningURI);
  }, [newQueryData, newQueryLoading, newQueryErrors, newQueryNetworkError]);

  function totpKey(url: string): string | null {
    const match = url.match(/secret=([^&]+)/);
    return match ? decodeURIComponent(match[1]!) : null;
  }

  return (
    <>
      <Helmet>
        <title>Two-Factor Authentication</title>
      </Helmet>
      <PancakeStack css={{ background: 'background.surface' }}>
        <PancakeStack.Header css={{ borderBottom: 'solid 1px', borderColor: 'border' }}>
          <FlowHeaderWithSettings title="Two-Factor Authentication | 1 of 2" />
        </PancakeStack.Header>
        <PancakeStack.Main>
          <CenteredLayout size="medium" css={{ gap: 'space.400', padding: 'space.400' }}>
            <Heading as="h1" data-testid={testID}>
              Configure authenticator app.
            </Heading>
            <Box>
              <Text>
                {!!error && (
                  <Banner
                    css={{ marginBottom: 'space.400' }}
                    variant="critical"
                    data-testid={testID ? `${testID}-error` : ''}
                  >
                    <Banner.Content>
                      <Text as="p">{error}</Text>
                    </Banner.Content>
                    <Banner.Close onClick={() => setError(null)} />
                  </Banner>
                )}
                <p>
                  To set up on your phone, download your preferred authenticator app from the App Store or Google Play
                  Store. Then, open the app and scan the QR code below:
                </p>
                {!!totpProvisioningURI && (
                  <>
                    <Box
                      css={{
                        borderColor: 'border',
                        borderStyle: 'solid',
                        borderWidth: 'thin',
                        padding: 'space.400',
                        marginY: 'space.200',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <QRCodeSVG
                        value={totpProvisioningURI}
                        size={200}
                        data-testid={testID ? `${testID}-qr-code` : null}
                      />
                    </Box>
                    <Box css={{ marginY: 'space.400' }}>
                      <Label css={{ marginBottom: 'space.100' }} htmlFor="totpCode">
                        Or enter the code manually:
                      </Label>
                      <Box
                        css={{
                          backgroundColor: 'primitives.grey.10',
                          color: 'text.weak',
                          padding: 'space.200',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                        data-testid={testID ? `${testID}-totp-key` : null}
                      >
                        {totpKey(totpProvisioningURI)}
                        <Button
                          variant="ghostPrimary"
                          spacing="compact"
                          onClick={() => copy(totpKey(totpProvisioningURI) || '')}
                          data-testid={testID ? `${testID}-copy` : null}
                        >
                          Copy
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
                <Box css={{ display: 'flex', alignItems: 'center' }}>
                  <Button type="button" onClick={clickContinue} data-testid={testID ? `${testID}-continue` : null}>
                    Continue
                  </Button>
                  <SambaAutoLink to={urlBuilder.getSmsSetupFlow()} css={{ marginLeft: 'space.200' }}>
                    Confirm with phone number
                  </SambaAutoLink>
                </Box>
              </Text>
            </Box>
          </CenteredLayout>
        </PancakeStack.Main>
      </PancakeStack>
    </>
  );
};

export { StartStep };
